import {
  adminApiClient,
  extractDownloadFilename,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import type {
  AdminAuditLog,
  AdminOrderStateEventList,
  ApiResponse,
  PaginatedResponse,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import {
  buildPaginatedResponse,
  extractArrayFromApiPayload,
} from '@/services/admin/paginatedResponseHelpers'

const normalizePaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  options: { keys?: string[]; defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const payloadAny = payload as any
  const items = extractArrayFromApiPayload<T>(payloadAny, {
    keys: options.keys,
    candidateKeys: ['items', 'records', 'deployments', 'logs'],
    includePagination: true,
  })
  return buildPaginatedResponse(payload, items, options)
}

export interface AuditLogParams {
  page?: number
  limit?: number
  admin_user_id?: string
  action?: string
  resource_type?: string
  resource_id?: string
}

export interface OrderStateEventParams {
  page?: number
  limit?: number
  orderId?: string
  eventType?: string
  source?: string
  paymentLogId?: string
}

export const auditService = {
  async getOrderStateEvents(
    params?: OrderStateEventParams,
    mode?: NetworkEnvironment
  ): Promise<AdminOrderStateEventList> {
    const response = await adminApiClient.get<ApiResponse<AdminOrderStateEventList>>(
      '/admin/audit-logs/order-state-events',
      {
        params,
        ...buildModeAwareConfig(mode),
      }
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.error || 'Failed to load order state events')
    }
    return response.data.data
  },

  async getAuditLogs(
    params?: AuditLogParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<AdminAuditLog>> {
    const searchParams = new URLSearchParams()

    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.admin_user_id) searchParams.append('admin_user_id', params.admin_user_id)
    if (params?.action) searchParams.append('action', params.action)
    if (params?.resource_type) searchParams.append('resource_type', params.resource_type)
    if (params?.resource_id) searchParams.append('resource_id', params.resource_id)

    const query = searchParams.toString()
    const url = query ? `/admin/audit-logs?${query}` : '/admin/audit-logs'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    return normalizePaginatedResponse<AdminAuditLog>(response.data, {
      keys: ['logs'],
      defaultPage: params?.page,
      defaultLimit: params?.limit,
    })
  },

  async exportCsv(
    params?: AuditLogParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/audit-logs/export', {
        params,
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export audit logs'))
    }
  },
}
